import React, { PropsWithChildren, useState } from "react"
import { Slider as ArkSlider, SliderRootProps } from "@ark-ui/react"
import cx from "classnames"
import { FieldWrapper, FieldWrapperProps } from "./field"

export type SliderPropsType<T extends number | number[]> = {
  /**
   * mandatory for A11y
   */
  value?: T
  defaultValue?: T
  ariaLabel: string
  /**
   * values interval
   */
  step?: number
  disabled?: boolean
  /**
   * Listener called when the value is changed
   */
  onValueChange?: (newValue: T) => void
  /**
   * Function used to visually display the slider value
   */
  renderValue?: (
    value: T,
    status: FieldWrapperProps["status"]
  ) => React.ReactElement

  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
} & FieldWrapperProps &
  Omit<SliderRootProps, "value" | "defaultValue" | "onValueChange">

const Slider = <T extends number | number[]>({
  defaultValue: _defaultValue,
  value: _value,
  min,
  max,
  disabled,
  onValueChange,
  renderValue,
  ariaLabel,
  hint,
  label,
  status,
  className,
  ...restProps
}: SliderPropsType<T>) => {
  const value: number[] | undefined = Array.isArray(_value)
    ? _value
    : _value !== undefined && !isNaN(_value)
      ? [_value]
      : undefined
  const defaultValue: number[] | undefined = Array.isArray(_defaultValue)
    ? _defaultValue
    : _defaultValue !== undefined && !isNaN(_defaultValue)
      ? [_defaultValue]
      : undefined
  function initValue(): number[] {
    if (value !== undefined) return value
    if (defaultValue !== undefined) return defaultValue
    if (min !== undefined) return [min]
    return [0]
  }

  const [sliderValue, setSliderValue] = useState(() => initValue())

  return (
    <FieldWrapper {...restProps} status={status} label={label} hint={hint}>
      <ArkSlider.Root
        {...restProps}
        className={cx("cobalt-slider", className, {
          "cobalt-slider--disabled": disabled,
          "cobalt-slider--withHint": hint,
        })}
        thumbAlignment="contain"
        disabled={disabled}
        aria-label={[ariaLabel]}
        value={sliderValue}
        min={min}
        max={max}
        // Need to force disbplaying in smaller spaces like popovers
        thumbSize={{
          width: 24,
          height: 24,
        }}
        onValueChange={({ value: newValueArr }) => {
          setSliderValue(newValueArr)
          const newValue =
            newValueArr.length === 1 ? newValueArr[0] : newValueArr
          onValueChange && onValueChange(newValue as T)
        }}
      >
        <ArkSlider.Label className={FieldWrapper.labelClassName}>
          {label}
        </ArkSlider.Label>
        {renderValue && (
          <div className="cobalt-slider__value-container">
            {renderValue(
              (sliderValue.length === 1 ? sliderValue[0] : sliderValue) as T,
              status
            )}
          </div>
        )}
        <ArkSlider.Control>
          <ArkSlider.Track>
            <ArkSlider.Range />
          </ArkSlider.Track>
          {sliderValue.map((_v, i) => (
            <ArkSlider.Thumb key={i} index={i}>
              <ArkSlider.HiddenInput />
            </ArkSlider.Thumb>
          ))}
        </ArkSlider.Control>
      </ArkSlider.Root>
    </FieldWrapper>
  )
}

export default Slider

export const SliderValueMeta = ({ children }: PropsWithChildren) => {
  return <span className="cobalt-slider__value-meta">{children}</span>
}
